/*
 * The Activity feed's copy. The frame is core's — the chips, the triage, the counters — while the
 * title of a row comes from the module that registered its kind ("chat.activity.mention"), which is
 * why there is no copy here for any one kind. A chip is named after the group its kinds registered
 * under, so a new group needs one line here and nothing else.
 *
 * A row's sentence is written in pieces — the lead, the tail, the message it points at — because the
 * person's name is drawn differently from the rest of the line (X-A3 V16): "Bea Bergmann" + "in
 * #planung · 23:34 · mention". The pieces are phrases and not words, so a translator sees a whole
 * clause, and they are assembled in the order English writes them, which is the one order this
 * build has. A heading is no longer written here at all: a day is named by `formatDay`, the one
 * place in the product that spells a date (D-10 P2).
 */

export const en = {
	"activity.title": "Activity",
	"activity.subtitle":
		"Everything addressed to you: mentions, replies, reactions and direct messages.",
	"activity.unread": "{count} unread",
	"activity.allRead": "Nothing unread",
	"activity.filter.label": "Filter",
	"activity.filter.all": "Everything",
	"activity.filter.mentions": "Mentions",
	"activity.filter.reactions": "Reactions",
	"activity.filter.dms": "Direct messages",
	"activity.filter.channels": "Channels",
	"activity.filter.invites": "Added to a conversation",
	"activity.filter.documents": "Documents",
	// The instance's own group: rows teamBase writes about itself rather than about a conversation
	// or a document — today the two the AI worker's absence and return write, and only to
	// administrators.
	"activity.filter.core": "System",
	"activity.markAll": "Mark all as read",
	// The button has always marked the open chip alone, so outside *Everything* it says what it does.
	"activity.markAll.filtered": "Mark these as read",
	"activity.markAll.done": "{count} marked as read",
	"activity.markAll.undo": "Undo",
	"activity.item.generic": "New activity",
	// Several people on one row, where naming all of them would run off the line.
	"activity.item.actors.more": "{names} and {count} more",
	"activity.item.readOf": "Mark {who}’s {what} as read",
	"activity.item.unreadOf": "Mark {who}’s {what} as unread",
	// A row whose owner named its kind in its own words ("Claude asks to change code").
	"activity.item.readKind": "Mark “{what}” as read",
	"activity.item.unreadKind": "Mark “{what}” as unread",
	"activity.item.someone": "somebody",
	"activity.item.via": "via @{group}",
	"activity.item.in": "in {conversation}",
	"activity.item.inDirect": "in a direct message",
	// The singular is insurance rather than a sentence anybody reads today: the row is only drawn with
	// a count above one (`rowSentence`), and one message reads "Bea Bergmann in #hiring" instead.
	"activity.item.sentCount": "sent {count} messages in {conversation}",
	"activity.item.sentCount.one": "sent a message in {conversation}",
	"activity.item.dmCount": "sent you {count} messages",
	"activity.item.dmCount.one": "sent you a message",
	// A reaction row carries the emoji themselves, which is what it is about; the second spelling runs
	// into the message it points at, and the first is for a row whose message could not be previewed.
	"activity.item.reacted": "reacted {emoji}",
	"activity.item.reactedTo": "reacted {emoji} to",
	// And the row that stands for more reactions than it can name — the server keeps one row per
	// message with a count on it and the payload of the *first* reaction, so the number is the subject
	// and nobody is named. Never one of them, by construction: a row that knows all its reactions uses
	// the two spellings above.
	"activity.item.reactions": "{count} reactions",
	"activity.item.reactionsTo": "to",
	// The number is the count of *new* replies, not the length of the thread, so the sentence says so
	// (X-B1 D15): a chip that read "9 replies" under a thread of eleven was simply wrong.
	"activity.item.replies": "{count} new replies in {author}’s thread",
	"activity.item.replies.one": "a new reply in {author}’s thread",

	// ---- T-07: the read state of a thread's row, and what a key press did ----
	//
	// Read, the row is no longer news: the thread is the subject and the number is what it brought
	// (X-B1 D15, the other half of the sentence above). The two `marked` lines are what `r` says out
	// loud — the key changes a bar and a weight, which a screen reader notices as nothing at all
	// (X-B3 A2) — and they are spoken by the page rather than declared on the binding, because which
	// of the two it is depends on what the press did.
	"activity.item.marked.read": "Marked as read.",
	"activity.item.marked.unread": "Marked as unread.",
	"activity.item.replies.read": "{author}’s thread · {count} replies",
	"activity.item.replies.read.one": "{author}’s thread · one reply",
	"activity.item.fromUnknown": "In {conversation}",
	// Not "you have left": the feed cannot tell a conversation somebody left from one they were never
	// in — a public channel whose name it has no row for reads the same way — and search says the same
	// thing about the same case (X-B1 D5).
	"activity.conversation.unknown": "A conversation you are not in",
	"activity.conversation.unknown.inline": "a conversation you are not in",
	// A label says what the key does and never which key it is — the sheet prints the chord in a chip
	// beside it (keyboard.md § Adding one, rule 2). Enter is the one key a label may name, because it
	// is deliberately not a binding (see `shortcuts.ts`) and the sheet would otherwise never mention
	// it; the arrow keys move the roving focus inside the list and are the widget's own, not chords.
	"activity.shortcut.next": "Move to the next item (Enter opens it)",
	"activity.shortcut.previous": "Move to the previous item",
	"activity.shortcut.toggleRead": "Mark the item read, or unread again",
	"activity.more": "Show older",
	"activity.loading": "Loading your activity …",
	"activity.empty.title": "Nothing unread.",
	"activity.empty": "Mentions, replies, reactions and direct messages land in this list.",
	"activity.empty.open": "Go to {conversation}",
	"activity.empty.filtered": "Nothing under this filter.",
	"activity.empty.clear": "Clear the filter",

	// ---- V-10: the threads view (X-A4 N2) ----
	//
	// The *Threads* chip is the list of threads — the replies group under the name its view carries —
	// so under it the page says what it is showing instead of repeating what Activity is in general.
	"activity.threads.subtitle":
		"One row per thread you take part in, the threads with new replies first.",
	"activity.threads.title": "Threads",

	// ---- The two operator items of the worker machines (S-06 §3.1, S-09 §5) ----
	//
	// Written by the server to the administrators alone: once when the machine that carries the AI
	// chats has not called in for the configured span, once when its stream says hello again. The
	// number is the instance's own setting, sent with the item, so the sentence stays true when it
	// is changed.
	"activity.core.work.unavailable.label": "AI unavailable",
	"activity.core.work.unavailable.title": "The AI machine has not called in for {minutes} minutes",
	"activity.core.work.unavailable.body":
		"AI chats wait until it is back. Nothing anybody wrote is lost.",
	"activity.core.work.back.label": "AI back",
	"activity.core.work.back.title": "The AI machine is back",
} as const satisfies Record<string, string>
